/**
 * CreativeSizeLimitPage — tenant-scoped creative upload size limit.
 *
 * Flow:
 * - Shows the current effective ceiling (tenant override or platform default).
 * - Admins can raise/lower the ceiling within [MIN_MAX, MAX_MAX] MB.
 * - Admins can reset back to the platform default.
 * - Preflight "check" tool: dry-run a prospective upload (bytes + mime).
 *
 * Brand: green only. Light theme. Zinc borders. 6-8px rounding.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCreativeSizeLimitCheck,
	useCreativeSizeLimitEffective,
	useCreativeSizeLimitReset,
	useCreativeSizeLimitUpdate,
} from "./hooks";
import type { CheckLimitResponse } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)] p-5";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const ONE_MB = 1024 * 1024;

function bytesToMB(bytes: number): number {
	if (!Number.isFinite(bytes) || bytes <= 0) return 0;
	return Math.round((bytes / ONE_MB) * 100) / 100;
}

function mbToBytes(mb: number): number {
	return Math.round(mb * ONE_MB);
}

function humanMime(mime: string | undefined): string {
	return mime && mime.trim().length > 0 ? mime : "(any)";
}

function checkBadgeClass(ok: boolean): string {
	return ok
 ? "inline-flex items-center rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800"
 : "inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800";
}

function reasonCopy(reason: string): string {
	switch (reason) {
 case "too_large":
 return "Above the tenant ceiling.";
 case "negative_size":
 return "Size cannot be negative.";
 case "invalid_size":
 return "Size must be a non-negative integer.";
 case "mime_not_allowed":
 return "Mime type not in allow-list.";
 default:
 return reason;
	}
}

export function CreativeSizeLimitPage() {
	const effectiveQ = useCreativeSizeLimitEffective();
	const updateMut = useCreativeSizeLimitUpdate();
	const resetMut = useCreativeSizeLimitReset();
	const checkMut = useCreativeSizeLimitCheck();

	const eff = effectiveQ.data;

	const [mbInput, setMbInput] = useState("");
	const [overridable, setOverridable] = useState(true);
	const [checkMB, setCheckMB] = useState("");
	const [checkMime, setCheckMime] = useState("");
	const [checkResult, setCheckResult] = useState<CheckLimitResponse | null>(
 null,
	);

	const bounds = useMemo(() => {
 if (!eff) return { min: 1, max: 500, def: 50 };
 return {
 min: bytesToMB(eff.min_max_bytes),
 max: bytesToMB(eff.max_max_bytes),
 def: bytesToMB(eff.default_max_bytes),
 };
	}, [eff]);

	const handleUpdate = (e: FormEvent) => {
 e.preventDefault();
 const mb = Number(mbInput);
 if (!Number.isFinite(mb) || mb <= 0) return;
 const max_bytes = mbToBytes(mb);
 updateMut.mutate(
 { max_bytes, overridable },
 { onSuccess: () => setMbInput("") },
 );
	};

	const handleReset = () => {
 resetMut.mutate();
	};

	const handleCheck = (e: FormEvent) => {
 e.preventDefault();
 const mb = Number(checkMB);
 if (!Number.isFinite(mb) || mb < 0) return;
 const size_bytes = mbToBytes(mb);
 const mime = checkMime.trim() || undefined;
 checkMut.mutate(
 { size_bytes, mime },
 { onSuccess: (d) => setCheckResult(d) },
 );
	};

	return (
 <main
 data-testid="creative-size-limit-page"
 className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 Creative size limit
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Upload ceiling
 </h1>
 <p className="text-sm text-zinc-600">
 Tenant-wide cap on creative upload size. Bounded{""}
 {bounds.min} MB to {bounds.max} MB.
 </p>
 </header>

 <section className={CARD} aria-label="Current effective limit">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Effective limit</h2>
 {effectiveQ.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-size-limit-loading"
 >
 Loading.
 </p>
 ) : effectiveQ.error ? (
 <p
 className={`mt-3 ${ERROR_TEXT}`}
 role="alert"
 data-testid="creative-size-limit-error"
 >
 {effectiveQ.error.message}
 </p>
 ) : eff ? (
 <dl
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
 data-testid="creative-size-limit-effective"
 >
 <div>
 <dt className={HINT}>Ceiling</dt>
 <dd
 className="text-lg font-semibold text-zinc-900"
 data-testid="creative-size-limit-label"
 >
 {eff.label}
 </dd>
 </div>
 <div>
 <dt className={HINT}>Source</dt>
 <dd className="text-sm text-zinc-700">
 {eff.is_default ? (
 <span data-testid="creative-size-limit-source-default">
 Platform default
 </span>
 ) : (
 <span data-testid="creative-size-limit-source-tenant">
 Tenant override
 </span>
 )}
 </dd>
 </div>
 <div>
 <dt className={HINT}>Mutable</dt>
 <dd className="text-sm text-zinc-700">
 {eff.overridable ? "Yes" : "Frozen"}
 </dd>
 </div>
 </dl>
 ) : null}
 </section>

 <section className={CARD} aria-label="Update ceiling">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Set ceiling</h2>
 <p className={`mt-1 ${HINT}`}>
 Admin only. Value in whole MB, clamped server-side to [
 {bounds.min}, {bounds.max}].
 </p>
 <form
 onSubmit={handleUpdate}
 className="mt-3 flex flex-col gap-3"
 data-testid="creative-size-limit-update-form"
 >
 <div className="flex flex-col gap-1">
 <label className={LABEL} htmlFor="csl-mb">
 Max size (MB)
 </label>
 <input
 id="csl-mb"
 type="number"
 inputMode="numeric"
 className={INPUT}
 placeholder={String(bounds.def)}
 value={mbInput}
 min={bounds.min}
 max={bounds.max}
 onChange={(e) => setMbInput(e.currentTarget.value)}
 disabled={updateMut.isPending}
 data-testid="creative-size-limit-mb-input"
 />
 </div>
 <label className="flex items-center gap-2 text-sm text-zinc-700">
 <input
 type="checkbox"
 checked={overridable}
 onChange={(e) => setOverridable(e.currentTarget.checked)}
 disabled={updateMut.isPending}
 data-testid="creative-size-limit-overridable-input"
 className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600/20"
 />
 Allow future tenant admins to change this ceiling
 </label>
 <div className="flex flex-wrap gap-2">
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={updateMut.isPending || !mbInput.trim()}
 data-testid="creative-size-limit-update-submit"
 >
 {updateMut.isPending ? "Saving." : "Save ceiling"}
 </button>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={handleReset}
 disabled={resetMut.isPending}
 data-testid="creative-size-limit-reset"
 >
 {resetMut.isPending ? "Resetting." : "Reset to default"}
 </button>
 </div>
 {updateMut.error ? (
 <p className={ERROR_TEXT} role="alert">
 {updateMut.error.message}
 </p>
 ) : null}
 {resetMut.error ? (
 <p className={ERROR_TEXT} role="alert">
 {resetMut.error.message}
 </p>
 ) : null}
 </form>
 </section>

 <section className={CARD} aria-label="Check upload">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Pre-flight check
 </h2>
 <p className={`mt-1 ${HINT}`}>
 Dry-run a prospective upload against the current ceiling.
 </p>
 <form
 onSubmit={handleCheck}
 className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end"
 data-testid="creative-size-limit-check-form"
 >
 <div className="flex flex-1 flex-col gap-1">
 <label className={LABEL} htmlFor="csl-check-mb">
 Prospective size (MB)
 </label>
 <input
 id="csl-check-mb"
 type="number"
 inputMode="decimal"
 className={INPUT}
 placeholder="10"
 value={checkMB}
 min={0}
 onChange={(e) => setCheckMB(e.currentTarget.value)}
 disabled={checkMut.isPending}
 data-testid="creative-size-limit-check-mb-input"
 />
 </div>
 <div className="flex flex-1 flex-col gap-1">
 <label className={LABEL} htmlFor="csl-check-mime">
 Mime (optional)
 </label>
 <input
 id="csl-check-mime"
 type="text"
 className={INPUT}
 placeholder="image/png"
 value={checkMime}
 onChange={(e) => setCheckMime(e.currentTarget.value)}
 disabled={checkMut.isPending}
 data-testid="creative-size-limit-check-mime-input"
 />
 </div>
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={checkMut.isPending || !checkMB.trim()}
 data-testid="creative-size-limit-check-submit"
 >
 {checkMut.isPending ? "Checking." : "Check"}
 </button>
 </form>
 {checkMut.error ? (
 <p className={`mt-2 ${ERROR_TEXT}`} role="alert">
 {checkMut.error.message}
 </p>
 ) : null}
 {checkResult ? (
 <div
 className="mt-3 flex flex-col gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3"
 data-testid="creative-size-limit-check-result"
 >
 <div className="flex items-center gap-2">
 <span className={checkBadgeClass(checkResult.ok)}>
 {checkResult.ok ? "Allowed" : "Blocked"}
 </span>
 <span className="text-xs text-zinc-600">
 Ceiling: {checkResult.label} (
 {humanMime(checkMime.trim() || undefined)})
 </span>
 </div>
 {checkResult.ok ? (
 <p className={HINT} data-testid="creative-size-limit-check-ok">
 Remaining headroom: {bytesToMB(checkResult.remaining_bytes)} MB
 </p>
 ) : (
 <p
 className={ERROR_TEXT}
 data-testid="creative-size-limit-check-fail"
 >
 {reasonCopy(checkResult.reason)}
 </p>
 )}
 </div>
 ) : null}
 </section>
 </main>
	);
}

export default CreativeSizeLimitPage;
